import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft, Loader2 } from "lucide-react";
import { ClubDashboardBody } from "./ClubDashboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CoachClubDashboard() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const club = params.get("club") || "";
  const team = params.get("team") || "";
  const token = localStorage.getItem("coachToken");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback((teamFilter) => {
    const q = `?club=${encodeURIComponent(club)}${teamFilter ? `&team=${encodeURIComponent(teamFilter)}` : ""}`;
    axios.get(`${API}/coach/club-dashboard${q}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setData(data))
      .catch((err) => {
        if (err?.response?.status === 401 || err?.response?.status === 403) { navigate("/coach", { replace: true }); return; }
        toast.error("Couldn't load the team dashboard.");
      })
      .finally(() => setLoading(false));
  }, [club, token, navigate]);

  useEffect(() => {
    if (!token) { navigate("/coach", { replace: true }); return; }
    if (!club) { navigate("/coach", { replace: true }); return; }
    load(team);
    // eslint-disable-next-line
  }, []);

  const onSelectTeam = (t) => {
    setLoading(true);
    const p = new URLSearchParams({ club });
    if (t) p.set("team", t);
    setParams(p, { replace: true });
    load(t);
  };

  if (loading && !data) {
    return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-white/50"><Loader2 className="animate-spin mr-2" size={18} /> Loading dashboard…</div>;
  }
  if (!data) return null;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden" data-testid="coach-club-dashboard">
      <div className="border-b border-white/10 bg-[#0d0d0d]/80 backdrop-blur sticky top-0 z-20 safe-top">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-[#C41E24]/15 border border-[#C41E24]/40 flex items-center justify-center flex-shrink-0">
              <ShieldCheck size={20} className="text-[#C41E24]" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C41E24]">Coach Mode · Live View</div>
              <h1 className="font-headings text-xl sm:text-2xl uppercase tracking-wide leading-none truncate">{data.club_name}</h1>
            </div>
          </div>
          <button data-testid="coach-view-back" onClick={() => navigate("/coach")}
            className="flex items-center gap-1.5 text-white/50 hover:text-white text-xs font-bold uppercase tracking-wider px-3 py-2 border border-white/10 rounded-md hover:border-white/30 transition-colors">
            <ArrowLeft size={14} /> <span className="hidden sm:inline">Back to Coach</span>
          </button>
        </div>
      </div>
      <ClubDashboardBody data={data} onSelectTeam={onSelectTeam}
        onPlayerClick={(id) => navigate(`/coach?tab=players&player=${id}&from=${encodeURIComponent(`/coach/club-view?${params.toString()}`)}`)} />
    </div>
  );
}
